import { useEffect, useState, useRef } from "react";
import { Form, Table } from "react-bootstrap";
import useApp from '../AppContext';
import { loadListObject, loadDataObject } from "./CalendarUtils";
import PaginationControls from "../Components/PaginationControls";
import { t } from "./../i18n";
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import RowsPerPageSelector from '../Components/RowsPerPageSelector';

function List3pls(props) {
    const app = useApp();
    const searchInputRef = useRef(null);
    const [serverUrl] = useState(app.serverUrl);
    const [userid] = useState(app.sessionToken?.UserId);
    const [data, setData] = useState([]);
    const [allColumnsObj, setAllColumnsObj] = useState({});
    const [isLoad, setIsLoad] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isDataFetched, setIsDataFetched] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [isDebouncing, setIsDebouncing] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(data.length / itemsPerPage);

    useEffect(() => {
        if (props.showModal && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [props.showModal]);

    useEffect(() => {
        async function getDefaultLayouts() {
            if (!serverUrl) return;

            if (!allColumnsObj || (Object.keys(allColumnsObj).length === 0)) {
                const objectName = "Yms_3pls";
                const allobj = await loadListObject(serverUrl, props.userToken, objectName);
                setAllColumnsObj(prev => ({ ...prev, [objectName]: allobj }));
            }
        }
        getDefaultLayouts();
    }, [serverUrl, props.userToken, allColumnsObj]);

    const fetch3pls = async (searchText = '') => {
        if (!userid || !app.sessionToken?.Role || Object.keys(allColumnsObj).length === 0) return;

        const objectName = "Yms_3pls";

        let defaultVariables = {
            filterName: "customfilter",
        };

        let prms = [];
        let prmValues = {};
        let baseFilter = "";

        if (searchText && searchText.trim() !== '') {
            const searchFilter = "(UPPER(Yms3pl.YMS3PLCODE) LIKE UPPER(?(;s;;p_SearchTerm)) OR " +
                "UPPER(Yms3pl.DESCR1) LIKE UPPER(?(;s;;p_SearchTerm2)))";

            if (baseFilter) {
                defaultVariables["appendFilter"] = `(${baseFilter}) AND ${searchFilter}`;
            } else {
                defaultVariables["appendFilter"] = searchFilter;
            }

            const searchPattern = `%${searchText.trim()}%`;
            prms.push({ parameterName: "p_SearchTerm", type: "" });
            prmValues["p_SearchTerm"] = searchPattern;
            prms.push({ parameterName: "p_SearchTerm2", type: "" });
            prmValues["p_SearchTerm2"] = searchPattern;
        } else if (baseFilter) {
            defaultVariables["appendFilter"] = baseFilter;
        }

        try {
            const loadingState = searchText ? setIsSearching : setIsLoad;
            loadingState(true);

            const responseObj = await loadDataObject(
                serverUrl,
                props.userToken,
                objectName,
                allColumnsObj,
                prms,
                prmValues,
                defaultVariables
            );

            if (responseObj) {
                setData(responseObj);
                setIsDataFetched(true);
                setCurrentPage(1);
            }
        } catch (error) {
            console.error("Error fetching 3PLs:", error);
        } finally {
            const loadingState = searchText ? setIsSearching : setIsLoad;
            loadingState(false);
        }
    };

    const fetchAll3pls = () => fetch3pls('');

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        if (value.trim() === '') {
            setData([]);
            setIsDataFetched(false);
            setCurrentPage(1);
            setIsDebouncing(false);
            return;
        }
        setIsDebouncing(true);

        const newTimeout = setTimeout(() => {
            setIsDebouncing(false);
            fetch3pls(value);
        }, 2000);

        setSearchTimeout(newTimeout);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && searchTerm.trim() !== '') {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
            setIsDebouncing(false);
            fetch3pls(searchTerm);
        }
    };

    return (
        <>
            <div className="p-4">
                <div className="list-modal-grid">
                    <div className="list-modal-left">
                        {data.length > 0 && data.filter(threePl => props.findSelect3pl && props.findSelect3pl(threePl)).length > 1 && (
                            <button
                                onClick={() => {
                                    data.forEach(threePl => {
                                        if (props.findSelect3pl && props.findSelect3pl(threePl)) {
                                            props.handleSelect3pl(threePl);
                                        }
                                    });
                                }}
                                className="btn btn-outline-secondary"
                                style={{
                                    padding: '6px 12px',
                                    fontSize: '14px',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    whiteSpace: 'nowrap',
                                    fontWeight: '500'
                                }}
                            >
                                <RemoveDoneIcon style={{ fontSize: '15px' }} />
                                {t('common.deselectAll', props.language)}
                            </button>
                        )}
                    </div>

                    <div className="list-modal-center">
                        <div className="searchBar-wrapper">
                            <i className={`bi ${isSearching ? 'bi-arrow-clockwise' : 'bi-search'}`}
                                style={{
                                    animation: isSearching ? 'spin 1s linear infinite' : 'none',
                                    color: isSearching ? 'var(--color-main-background)' : 'inherit'
                                }} />
                            <input
                                type="text"
                                placeholder={t('common.search', props.language)}
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                onKeyPress={handleKeyPress}
                                ref={searchInputRef}
                            />
                            {searchTerm && (
                                <i
                                    className="bi bi-x-circle"
                                    onClick={() => handleSearchChange('')}
                                />
                            )}
                        </div>

                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={fetchAll3pls}
                                disabled={isLoad || Object.keys(allColumnsObj).length === 0}
                                className="btn btn-primary"
                                style={{
                                    padding: '3px 7px',
                                    fontSize: '19px',
                                    borderRadius: '50%',
                                    backgroundColor: 'var(--color-main-background)',
                                    color: 'white',
                                    border: 'none',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                {isLoad ? (
                                    <i className="bi bi-arrow-clockwise" style={{ animation: 'spin 1s linear infinite' }}></i>
                                ) : (
                                    <i className="bi bi-arrow-clockwise"></i>
                                )}
                            </button>
                            {Object.keys(allColumnsObj).length === 0 && (
                                <p style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '8px', color: '#666', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                    Loading metadata...
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="list-modal-right">
                        <RowsPerPageSelector
                            isDataFetched={isDataFetched}
                            dataLength={data.length}
                            itemsPerPage={itemsPerPage}
                            setItemsPerPage={setItemsPerPage}
                            setCurrentPage={setCurrentPage}
                            language={props.language}
                        />
                    </div>

                    <div className="list-modal-mobile-rows">
                        <RowsPerPageSelector
                            isDataFetched={isDataFetched}
                            dataLength={data.length}
                            itemsPerPage={itemsPerPage}
                            setItemsPerPage={setItemsPerPage}
                            setCurrentPage={setCurrentPage}
                            language={props.language}
                        />
                    </div>
                </div>
            </div>
            <div style={{ height: "250px", borderRadius: '22px', overflowY: "hidden" }}>
                <Table striped hover className='table-custom-pos'>
                    <thead>
                        <tr>
                            <th>{t('list3pls.code', props.language)}</th>
                            <th>{t('list3pls.description', props.language)}</th>
                            <th>{t('list3pls.socialSecurityNo', props.language)}</th>
                            <th>{t('common.select', props.language)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isSearching || isDebouncing ? (
                            <tr>
                                <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                                    <i className="bi bi-arrow-clockwise" style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }}></i>
                                    {t('common.search', props.language)}...
                                </td>
                            </tr>
                        ) : currentItems.length === 0 ? (
                            <tr>
                                <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                                    {searchTerm && !isDebouncing ? (
                                        <div>
                                            {t('common.noData', props.language)}
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', width: '375px', margin: 'auto', justifyContent: 'center', gap: '8px', flexDirection: 'column' }}>
                                            <i className="bi bi-info-circle" style={{ fontSize: '40px', color: 'var(--color-main-background)' }}></i>
                                            {t('list3pls.clickToFetch', props.language)}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ) : (
                            currentItems.map((threePl) => {
                                const isSelected = props.findSelect3pl ? Boolean(props.findSelect3pl(threePl)) : false;
                                return (
                                    <tr
                                        key={threePl.Yms3plId}
                                        onClick={() => props.handleSelect3pl(threePl)}
                                        style={{
                                            cursor: 'pointer',
                                            borderLeft: isSelected ? '5px solid var(--color-main-background)' : '5px solid transparent',
                                            borderRight: '5px solid transparent',
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                        <td>#{threePl.Yms3plCode}</td>
                                        <td>{threePl.Descr1}</td>
                                        <td>{threePl.SocialSecurityNo}</td>
                                        <td className="text-center">
                                            <Form.Check
                                                type="checkbox"
                                                className='custom-checkbox'
                                                onClick={(e) => e.stopPropagation()}
                                                name="3plSelection"
                                                onChange={() => props.handleSelect3pl(threePl)}
                                                checked={isSelected}
                                            />
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </Table>
            </div>

            {isDataFetched && data.length > itemsPerPage && (
                <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => setCurrentPage(page)}
                />
            )}

            <div className="button-save-pos">
                <button onClick={props.handleSave3pls}>
                    <i className="bi bi-save me-1"></i> {t('common.select', props.language)}
                </button>
            </div>
        </>
    );
}

export default List3pls;
